import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

const IngecomSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // MQTT States
  const [mqttHost, setMqttHost] = useState<string>('');
  const [mqttPort, setMqttPort] = useState<string>('1883');
  const [mqttClientId, setMqttClientId] = useState<string>('');
  const [mqttTopic, setMqttTopic] = useState<string>('');
  const [mqttUsername, setMqttUsername] = useState<string>('');
  const [mqttPassword, setMqttPassword] = useState<string>(localStorage.getItem('mqttPassword') || '');
  const [mqttStatus, setMqttStatus] = useState<string>('unknown');
  const [mqttStatusError, setMqttStatusError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoadingMqtt, setIsLoadingMqtt] = useState<boolean>(false);
  const [isLoadingMqttStatus, setIsLoadingMqttStatus] = useState<boolean>(false);

  // SMTP States
  const [smtpServer, setSmtpServer] = useState<string>('');
  const [smtpPort, setSmtpPort] = useState<string>('');
  const [smtpUsername, setSmtpUsername] = useState<string>('');
  const [smtpPassword, setSmtpPassword] = useState<string>(localStorage.getItem('smtpPassword') || '');
  const [smtpSender, setSmtpSender] = useState<string>('');
  const [smtpUseTLS, setSmtpUseTLS] = useState<boolean>(true);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState<string>('');
  const [newBccRecipient, setNewBccRecipient] = useState<string>('');
  const [isLoadingSmtp, setIsLoadingSmtp] = useState<boolean>(false);

  // Password tracking states
  const [hasSavedMqttPassword, setHasSavedMqttPassword] = useState<boolean>(false);
  const [hasSavedSmtpPassword, setHasSavedSmtpPassword] = useState<boolean>(false);

  // Global save states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Initial values to track changes
  const [initialValues, setInitialValues] = useState({
    mqttHost: '',
    mqttPort: '1883',
    mqttClientId: '',
    mqttTopic: '',
    mqttUsername: '',
    mqttPassword: localStorage.getItem('mqttPassword') || '',
    smtpServer: '',
    smtpPort: '',
    smtpUsername: '',
    smtpPassword: localStorage.getItem('smtpPassword') || '',
    smtpSender: '',
    smtpUseTLS: true,
    emailRecipients: [] as string[],
    bccRecipients: [] as string[]
  });

  // Check for unsaved changes
  const checkForChanges = useCallback(() => {
    const currentValues = {
      mqttHost,
      mqttPort,
      mqttClientId,
      mqttTopic,
      mqttUsername,
      mqttPassword,
      smtpServer,
      smtpPort,
      smtpUsername,
      smtpPassword,
      smtpSender,
      smtpUseTLS,
      emailRecipients,
      bccRecipients
    };
    
    const hasChanges =
      currentValues.mqttHost !== initialValues.mqttHost ||
      currentValues.mqttPort !== initialValues.mqttPort ||
      currentValues.mqttClientId !== initialValues.mqttClientId ||
      currentValues.mqttTopic !== initialValues.mqttTopic ||
      currentValues.mqttUsername !== initialValues.mqttUsername ||
      currentValues.mqttPassword !== initialValues.mqttPassword ||
      currentValues.smtpServer !== initialValues.smtpServer ||
      currentValues.smtpPort !== initialValues.smtpPort ||
      currentValues.smtpUsername !== initialValues.smtpUsername ||
      currentValues.smtpPassword !== initialValues.smtpPassword ||
      currentValues.smtpSender !== initialValues.smtpSender ||
      currentValues.smtpUseTLS !== initialValues.smtpUseTLS ||
      JSON.stringify(currentValues.emailRecipients) !== JSON.stringify(initialValues.emailRecipients) ||
      JSON.stringify(currentValues.bccRecipients) !== JSON.stringify(initialValues.bccRecipients);

    setHasUnsavedChanges(hasChanges);
  }, [mqttHost, mqttPort, mqttClientId, mqttTopic, mqttUsername, mqttPassword, smtpServer, smtpPort, smtpUsername, smtpPassword, smtpSender, smtpUseTLS, emailRecipients, bccRecipients, initialValues]);

  // Check for changes whenever relevant state changes
  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]);

  // Intercept browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', location.pathname);
        showNavigationConfirmation(() => {
          setHasUnsavedChanges(false);
          window.history.back();
        });
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('popstate', handlePopState);
      // Push a dummy state to enable popstate detection
      window.history.pushState(null, '', location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname]);
  
  // Intercept navigation links
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      if (hasUnsavedChanges) {
        const target = e.target as HTMLElement;
        const linkElement = target.closest('a[href]') as HTMLAnchorElement;
        
        if (linkElement && linkElement.href) {
          // Check if it's an internal navigation link
          const currentOrigin = window.location.origin;
          const linkUrl = new URL(linkElement.href, currentOrigin);
          
          if (linkUrl.origin === currentOrigin && linkUrl.pathname !== location.pathname) {
            e.preventDefault();
            e.stopPropagation();
            
            showNavigationConfirmation(() => {
              setHasUnsavedChanges(false);
              // Use React Router navigation instead of window.location.href
              const path = linkElement.pathname;
              navigate(path);
            });
          }
        }
      }
    };

    if (hasUnsavedChanges) {
      document.addEventListener('click', handleLinkClick, true);
    }

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [hasUnsavedChanges, location.pathname, navigate]);

  // Load MQTT settings and SMTP settings
  useEffect(() => {
    const loadMqttSettings = async () => {
      try {
        setIsLoadingMqtt(true);
        const response = await apiClient.get('/settings/ingecom/mqtt');
        const data = response.data;
        const mqttData = {
          host: data.host || '',
          port: data.port?.toString() || '1883',
          client_id: data.client_id || '',
          topic: data.topic || '',
          username: data.username || '',
          password: localStorage.getItem('mqttPassword') || ''
        };
        
        setMqttHost(mqttData.host);
        setMqttPort(mqttData.port);
        setMqttClientId(mqttData.client_id);
        setMqttTopic(mqttData.topic);
        setMqttUsername(mqttData.username);
        setHasSavedMqttPassword(!!data.password);
        
        // Update initial values
        setInitialValues(prev => ({
          ...prev,
          mqttHost: mqttData.host,
          mqttPort: mqttData.port,
          mqttClientId: mqttData.client_id,
          mqttTopic: mqttData.topic,
          mqttUsername: mqttData.username,
          mqttPassword: mqttData.password
        }));
        
        loadMqttStatus();
      } catch (err) {
        setError(t('ingecomSettings.loadErrorMQTT'));
      } finally {
        setIsLoadingMqtt(false);
      }
    };

    const loadSmtpSettings = async () => {
      try {
        setIsLoadingSmtp(true);
        const response = await apiClient.get('/settings/email');
        const data = response.data;
        
        // Si le serveur indique qu'il n'y a pas de mot de passe sauvé, vider le localStorage
        let smtpPasswordValue = localStorage.getItem('smtpPassword') || '';
        if (!data.has_smtp_password) {
          localStorage.removeItem('smtpPassword');
          smtpPasswordValue = '';
          setSmtpPassword('');
        }
        
        const smtpData = {
          server: data.smtp_server || '',
          port: data.smtp_port?.toString() || '',
          username: data.smtp_username || '',
          password: smtpPasswordValue,
          sender: data.sender_email || '',
          useTLS: data.smtp_use_tls === undefined ? true : data.smtp_use_tls,
          recipients: data.recipients || [],
          bccRecipients: data.bcc_recipients || []
        };
        
        setSmtpServer(smtpData.server);
        setSmtpPort(smtpData.port);
        setSmtpUsername(smtpData.username);
        setSmtpSender(smtpData.sender);
        setSmtpUseTLS(smtpData.useTLS);
        setEmailRecipients(smtpData.recipients);
        setBccRecipients(smtpData.bccRecipients);
        setHasSavedSmtpPassword(data.has_smtp_password || false);
        
        // Update initial values
        setInitialValues(prev => ({
          ...prev,
          smtpServer: smtpData.server,
          smtpPort: smtpData.port,
          smtpUsername: smtpData.username,
          smtpPassword: smtpData.password,
          smtpSender: smtpData.sender,
          smtpUseTLS: smtpData.useTLS,
          emailRecipients: smtpData.recipients,
          bccRecipients: smtpData.bccRecipients
        }));
      } catch (err) {
        setError(t('ingecomSettings.loadErrorSMTP'));
      } finally {
        setIsLoadingSmtp(false);
      }
    };

    loadMqttSettings();
    loadSmtpSettings();
  }, [t]);

  // Load MQTT connection status
  const loadMqttStatus = async () => {
    try {
      setIsLoadingMqttStatus(true);
      const response = await apiClient.get('/settings/ingecom/mqtt/status');
      const data = response.data;
      setMqttStatus(data.status);
      setMqttStatusError(data.error);
      setIsConnected(data.is_connected);
    } catch (err) {
      setMqttStatus('error');
      setMqttStatusError(t('ingecomSettings.loadErrorMQTTStatus'));
      setIsConnected(false);
    } finally {
      setIsLoadingMqttStatus(false);
    }
  };

  // Save MQTT settings
  const handleSaveMqttSettings = async () => {
    try {
      setIsLoadingMqtt(true);
      
      // Create payload without password initially
      const payload: any = {
        host: mqttHost,
        port: mqttPort ? parseInt(mqttPort) : 1883,
        client_id: mqttClientId,
        topic: mqttTopic,
        username: mqttUsername,
      };
      
      // Only include password if it's not empty
      if (mqttPassword.trim() !== '') {
        payload.password = mqttPassword;
        setHasSavedMqttPassword(true);
      }
      
      await apiClient.put('/settings/ingecom/mqtt', payload);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        loadMqttStatus(); // Reload status after saving
      }, 1000);
    } catch (err) {
      setError(t('ingecomSettings.saveErrorMQTT'));
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setIsLoadingMqtt(false);
    }
  };

  // Helpers for recipients management
  const addRecipient = () => {
    if (newRecipient && !emailRecipients.includes(newRecipient)) {
      setEmailRecipients([...emailRecipients, newRecipient]);
      setNewRecipient('');
    }
  };

  const removeRecipient = (email: string) => {
    setEmailRecipients(emailRecipients.filter(r => r !== email));
  };

  const addBccRecipient = () => {
    if (newBccRecipient && !bccRecipients.includes(newBccRecipient)) {
      setBccRecipients([...bccRecipients, newBccRecipient]);
      setNewBccRecipient('');
    }
  };

  const removeBccRecipient = (email: string) => {
    setBccRecipients(bccRecipients.filter(r => r !== email));
  };

  // Save SMTP settings
  const handleSaveSmtpSettings = async () => {
    try {
      setIsLoadingSmtp(true);
      // Prépare les paramètres SMTP
      const smtpPayload: any = {
        smtp_server: smtpServer,
        smtp_port: smtpPort ? parseInt(smtpPort) : undefined,
        smtp_username: smtpUsername,
        sender_email: smtpSender,
        smtp_use_tls: smtpUseTLS,
      };
      
      // N'ajoute le mot de passe que s'il est renseigné
      if (smtpPassword.trim() !== '') {
        smtpPayload.smtp_password = smtpPassword;
        setHasSavedSmtpPassword(true);
      }
      
      // Mise à jour des paramètres SMTP via l'endpoint ingecom
      await apiClient.put('/settings/ingecom/smtp', smtpPayload);

      // Mise à jour des destinataires via l'endpoint email
      await apiClient.put('/settings/email', {
        recipients: emailRecipients,
        bcc_recipients: bccRecipients
      });

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError(t('ingecomSettings.saveErrorSMTP'));
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setIsLoadingSmtp(false);
    }
  };

  // Save all settings
  const handleSaveAllSettings = async () => {
    try {
      setIsSaving(true);
      
      // Save MQTT settings
      try {
        const mqttPayload: any = {
          host: mqttHost,
          port: mqttPort ? parseInt(mqttPort) : 1883,
          client_id: mqttClientId,
          topic: mqttTopic,
          username: mqttUsername,
        };
        
        if (mqttPassword.trim() !== '') {
          mqttPayload.password = mqttPassword;
          setHasSavedMqttPassword(true);
        }
        
        await apiClient.put('/settings/ingecom/mqtt', mqttPayload);
      } catch (mqttError) {
        // MQTT settings save failed, continue with SMTP
      }
      
      // Save SMTP settings
      try {
        const smtpPayload: any = {
          smtp_server: smtpServer,
          smtp_port: smtpPort ? parseInt(smtpPort) : undefined,
          smtp_username: smtpUsername,
          sender_email: smtpSender,
          smtp_use_tls: smtpUseTLS,
        };
        
        if (smtpPassword.trim() !== '') {
          smtpPayload.smtp_password = smtpPassword;
          setHasSavedSmtpPassword(true);
        }
        
        await apiClient.put('/settings/ingecom/smtp', smtpPayload);

        // Save recipients
        await apiClient.put('/settings/email', {
          recipients: emailRecipients,
          bcc_recipients: bccRecipients
        });
      } catch (smtpError) {
        // SMTP settings save failed
      }
      
      // Update initial values to current values
      setInitialValues({
        mqttHost,
        mqttPort,
        mqttClientId,
        mqttTopic,
        mqttUsername,
        mqttPassword,
        smtpServer,
        smtpPort,
        smtpUsername,
        smtpPassword,
        smtpSender,
        smtpUseTLS,
        emailRecipients,
        bccRecipients
      });
      
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      
      // Reload MQTT status after saving
      loadMqttStatus();
      
    } catch (error) {
      console.error('Failed to save settings:', error);
      setError(t('ingecomSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowConfirmDialog(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  const handleNavigationCancel = () => {
    setShowConfirmDialog(false);
  };

  // Handle save and navigate
  const handleSaveAndNavigate = async () => {
    let saveSuccess = false;
    
    try {
      setIsSaving(true);

      // Use the same logic as handleSaveAllSettings
      await handleSaveAllSettings();
      
      saveSuccess = true;

      // After successful save, proceed with navigation
      if (pendingNavigation) {
        pendingNavigation();
      }
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Failed to save before navigation:', error);
      // Keep dialog open if save fails
    } finally {
      setIsSaving(false);
      
      // Ensure navigation happens even if save fails
      if (!saveSuccess && pendingNavigation) {
        pendingNavigation();
        setShowConfirmDialog(false);
      }
    }
  };

  // Show navigation confirmation dialog
  const showNavigationConfirmation = (navigateFn: () => void) => {
    setPendingNavigation(() => navigateFn);
    setShowConfirmDialog(true);
  };

  // Close success message
  const handleCloseSuccessMessage = () => {
    setSaveSuccess(false);
  };

  // Determine MQTT status color
  const getStatusColor = () => {
    if (isConnected) {
      return 'success.main'; // Priority to successful connection
    }
    switch (mqttStatus) {
      case 'connecting':
        return 'warning.main';
      case 'disconnected':
        return 'grey.500';
      case 'error':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  if (user?.role !== 'ingecom') {
    return <Alert severity="error">{t('ingecomSettings.accessDenied')}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('ingecomSettings.title')}
      </Typography>
      {error && <Alert severity="error">{t('ingecomSettings.saveError')}</Alert>}
      {saveSuccess && <Alert severity="success">{t('ingecomSettings.saveSuccess')}</Alert>}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">{t('ingecomSettings.mqttSettings')}</Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {isLoadingMqtt ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttBrokerUrl')}
                  variant="outlined"
                  value={mqttHost}
                  onChange={(e) => setMqttHost(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttPort')}
                  variant="outlined"
                  value={mqttPort}
                  onChange={(e) => setMqttPort(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttClientId')}
                  variant="outlined"
                  value={mqttClientId}
                  onChange={(e) => setMqttClientId(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttTopic')}
                  variant="outlined"
                  value={mqttTopic}
                  onChange={(e) => setMqttTopic(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttUsername')}
                  variant="outlined"
                  value={mqttUsername}
                  onChange={(e) => setMqttUsername(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.mqttPassword')}
                  variant="outlined"
                  type="password"
                  value={mqttPassword}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setMqttPassword(newValue);
                    localStorage.setItem('mqttPassword', newValue);
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder={hasSavedMqttPassword ? "••••••••" : ""}
                  helperText={hasSavedMqttPassword && mqttPassword === '' ? t('ingecomSettings.passwordSaved') : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('ingecomSettings.mqttStatus')}
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getStatusColor(),
                          display: 'inline-block',
                          mr: 1,
                        }}
                      />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="body1">{mqttStatus}</Typography>
                      {mqttStatusError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          <strong>Error Details:</strong> {mqttStatusError}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={loadMqttStatus}
                        disabled={isLoadingMqttStatus}
                        startIcon={isLoadingMqttStatus ? <CircularProgress size={16} /> : null}
                      >
                        Refresh
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

            </>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">{t('ingecomSettings.smtpSettings')}</Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {isLoadingSmtp ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.smtpServer')}
                  variant="outlined"
                  value={smtpServer}
                  onChange={(e) => setSmtpServer(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.smtpPort')}
                  variant="outlined"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.smtpUsername')}
                  variant="outlined"
                  value={smtpUsername}
                  onChange={(e) => setSmtpUsername(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.smtpPassword')}
                  variant="outlined"
                  type="password"
                  value={smtpPassword}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSmtpPassword(newValue);
                    localStorage.setItem('smtpPassword', newValue);
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder={hasSavedSmtpPassword ? "••••••••" : ""}
                  helperText={hasSavedSmtpPassword && smtpPassword === '' ? t('ingecomSettings.passwordSaved') : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ingecomSettings.smtpSender')}
                  variant="outlined"
                  value={smtpSender}
                  onChange={(e) => setSmtpSender(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smtpUseTLS}
                      onChange={(e) => setSmtpUseTLS(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('ingecomSettings.smtpUseTLS')}
                />
              </Grid>
              
              {/* Gestion des destinataires BCC */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('ingecomSettings.bccRecipients')}
                </Typography>

                {/* Champ pour ajouter un destinataire */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    label={t('ingecomSettings.newBccRecipient')}
                    variant="outlined"
                    value={newBccRecipient}
                    onChange={(e) => setNewBccRecipient(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addBccRecipient();
                      }
                    }}
                    fullWidth
                    placeholder={t('ingecomSettings.bccRecipientPlaceholder') as string}
                    sx={{ mr: 1 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addBccRecipient}
                    disabled={!newBccRecipient.trim()}
                    sx={{ minWidth: '120px' }}
                  >
                    {t('common.add')}
                  </Button>
                </Box>

                {/* Liste des destinataires */}
                <Box sx={{ mt: 2 }}>
                  {bccRecipients.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('ingecomSettings.noBccRecipients')}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {bccRecipients.map((email) => (
                        <Chip
                          key={email}
                          label={email}
                          onDelete={() => removeBccRecipient(email)}
                          deleteIcon={<DeleteIcon />}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              
            </>
          )}
        </Grid>
      </Paper>

      {/* Global Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAllSettings}
          disabled={isSaving || !hasUnsavedChanges}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            boxShadow: hasUnsavedChanges ? 3 : 1,
            '&:hover': {
              boxShadow: hasUnsavedChanges ? 4 : 2,
            }
          }}
        >
          {isSaving ? t('ingecomSettings.saving') : t('ingecomSettings.saveAll')}
        </Button>
      </Box>

      {/* Success Message */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSuccessMessage}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('ingecomSettings.saveSuccess')}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleNavigationCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {t('settings.confirmDialog.saveTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {t('settings.confirmDialog.saveMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNavigationCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleNavigationConfirm} color="primary">
            {t('settings.confirmDialog.leaveWithoutSaving')}
          </Button>
          <Button
            onClick={handleSaveAndNavigate}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {isSaving ? t('settings.saving') : t('settings.confirmDialog.saveAndLeave')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IngecomSettings;